import { jsPDF } from 'jspdf';

const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

const formatDate = (d) => {
  if (!d) return '—';
  const t = d?.toDate ? d.toDate() : new Date(d);
  return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Generate and download a PDF for the given bid.
 * @param {Object} bid - Bid document (customerName, lineItems, total, notes, etc.)
 */
export function downloadBidPdf(bid) {
  const doc = new jsPDF({ format: 'a4', unit: 'mm' });
  const pageW = doc.internal.pageSize.getWidth();
  let y = 20;

  // Title
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('Bid / Estimate', 20, y);
  y += 12;

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Date: ${formatDate(bid.updatedAt || bid.createdAt)}`, 20, y);
  y += 10;

  // Customer
  doc.setFont(undefined, 'bold');
  doc.text('Prepared for:', 20, y);
  doc.setFont(undefined, 'normal');
  y += 6;
  doc.text(bid.customerName || '—', 20, y);
  y += 5;
  const contactLines = [bid.email, bid.phone, bid.address].filter(Boolean);
  contactLines.forEach((line) => {
    doc.text(line, 20, y);
    y += 5;
  });
  y += 8;

  // Line items table
  doc.setFont(undefined, 'bold');
  doc.text('Services', 20, y);
  y += 6;
  doc.setFont(undefined, 'normal');

  const colService = 20;
  const colDetails = 70;
  const colQty = 130;
  const colPrice = 150;
  const colTotal = 180;

  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text('Service', colService, y);
  doc.text('Details', colDetails, y);
  doc.text('Qty', colQty, y);
  doc.text('Unit $', colPrice, y);
  doc.text('Amount', colTotal, y);
  y += 6;
  doc.setFont(undefined, 'normal');

  const lineItems = bid.lineItems || [];
  lineItems.forEach((item) => {
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
    const qty = Number(item.quantity) || 1;
    const unit = parseFloat(item.unitPrice) || 0;
    const amount = qty * unit;
    doc.text(String(item.serviceType || '—').substring(0, 28), colService, y);
    doc.text(String(item.description || '').substring(0, 32), colDetails, y);
    doc.text(String(qty), colQty, y);
    doc.text(formatCurrency(unit), colPrice, y);
    doc.text(formatCurrency(amount), colTotal, y);
    y += 6;
  });

  y += 6;
  doc.setFont(undefined, 'bold');
  doc.text('Total:', colPrice, y);
  doc.text(formatCurrency(bid.total), colTotal, y);
  doc.setFont(undefined, 'normal');
  y += 10;

  if (bid.notes && String(bid.notes).trim()) {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    doc.setFont(undefined, 'bold');
    doc.text('Notes', 20, y);
    doc.setFont(undefined, 'normal');
    y += 5;
    const notes = doc.splitTextToSize(String(bid.notes).trim(), pageW - 40);
    doc.text(notes, 20, y);
    y += notes.length * 5 + 5;
  }

  const filename = `Bid-${(bid.customerName || 'estimate').replace(/[^a-z0-9-_]/gi, '-').substring(0, 40)}-${formatDate(bid.updatedAt || bid.createdAt).replace(/[\s,]/g, '-')}.pdf`;
  doc.save(filename);
}

/**
 * Plain-text summary of the bid for email body.
 */
export function getBidEmailBody(bid) {
  const lines = [
    `Bid for ${bid.customerName || 'Customer'}`,
    '',
    'Services:',
    ...(bid.lineItems || []).map((item) => {
      const qty = Number(item.quantity) || 1;
      const unit = parseFloat(item.unitPrice) || 0;
      return `  • ${item.serviceType || 'Service'}${item.description ? ` - ${item.description}` : ''}: ${qty} × ${formatCurrency(unit)} = ${formatCurrency(qty * unit)}`;
    }),
    '',
    `Total: ${formatCurrency(bid.total)}`,
    ''
  ];
  if (bid.notes && String(bid.notes).trim()) {
    lines.push('Notes: ' + String(bid.notes).trim(), '');
  }
  lines.push('— Driscollland');
  return lines.join('\n');
}

/**
 * Short summary for SMS (keep under ~160 chars for single segment if possible, or at least concise).
 */
export function getBidSmsBody(bid) {
  const total = formatCurrency(bid.total);
  const items = (bid.lineItems || []).map((i) => i.serviceType || 'Service').filter(Boolean);
  const list = items.length <= 2 ? items.join(', ') : `${items.length} services`;
  return `Bid for ${bid.customerName || 'you'}: ${list}. Total: ${total}. — Driscollland`;
}
